import IRootBuilderModel from "~/interfaces/builderModelInterfaces";
import store from "~/state/store";
import {IOrganizationDocument} from "~/interfaces/organizationDocumentInterfaces";
import Organization from "../organization";
import {IPeekerOptions} from "~/services/peeker";
import IRepo from "~/interfaces/repo";
import Repo from "~/state/modules/repo";
import MappingProvider from "~/providers/mappingProvider";
import MappingBuilderModel from "../tracked/mappingBuilderModel";
import {
  IMapping,
  IOriginWrapper,
  TMappingOriginType,
  TModelMappingOriginType
} from "~/interfaces/mappingInterfaces";
import Folder from "../../../formability/document_management/models/folder";
import MappingOrigin from "./mappingOrigin";
import ModelMapping from "./modelMapping";
import {RelationType} from "../relationType";
import {Hash} from "~/interfaces/hashInterfaces";
import Vue from "vue";
import {ObjectHelper} from "~/helpers/objectHelper";
import ModelMappingValue from "./modelMappingValue";
import MappingOriginForm from "./mappingOriginForm";
import MappingOriginSalesforce from "./mappingOriginSalesforce";
import MappingOriginObjectModel from "./mappingOriginObjectModel";
import {ModelType} from "~/enums/modelType";
import PublishedObjectModelDecorator from "~/models/decorators/publishedObjectModelDecorator";
import {IObjectModel} from "~/interfaces/objectModelInterfaces";
import PublishedMappingProvider from "~/providers/publishedMappingProvider";
import {Logger} from "~/services/logger";
import Validatable from "~/mixins/validatable";
import MappingBuilderValidation from "~/models/navigableValidations/mappingBuilderValidation";
import ErrorHandler from "~/models/errorHandling/errorHandler";
import ModelMappingHandler from "../../../formability/mappingBuilder/services/modelMappingHandler";

export default class Mapping extends Validatable(MappingBuilderModel) implements IRootBuilderModel, IOrganizationDocument, IMapping {
  displayName: string;
  _organization: Organization;
  _classKey = "mapping";
  attempted_to_publish?: boolean;
  author: any;
  folder_id: string;
  title: string;

  source: MappingOrigin;
  destination: MappingOrigin;

  locked_by: string;
  _publishedRecord: PublishedObjectModelDecorator;
  published_mapping_id?: string;

  except = ['mapping', 'createEmbeddedObject', 'organization', 'saveTimer', '_organization', 'source', 'destination'];

  get EMBEDDED_MODELS(): { [key: string]: { type: any, relationType: RelationType } } {
    return {
      'source': { type: MappingOrigin, relationType: RelationType.singleObject },
      'destination': { type: MappingOrigin, relationType: RelationType.singleObject },
      'model_mappings': {type: ModelMapping, relationType: RelationType.dictionary},
    };
  }

  createEmbeddedObject = {
    model_mappings: (parent, json): ModelMapping => {
      return new ModelMapping();
    },
    source: (parent, json) => {
      const type: TMappingOriginType = json.friendly_type;
      let obj: MappingOrigin;

      switch (type) {
        case ModelType.Form:
          obj = new MappingOriginForm();
          break;
        case ModelType.ObjectModel:
          obj = new MappingOriginObjectModel();
          break;
        case ModelType.SObject:
          obj = new MappingOriginSalesforce();
          break;
        default:
          // do not assertNever here - sometimes mapping is created without need for source/destination models
          break;
      }

      if (obj) obj._nameInParent = 'source';

      return obj;
    },
    destination: (parent, json) => {
      const type: TModelMappingOriginType = json.friendly_type;
      let obj: MappingOrigin;

      switch (type) {
        case ModelType.Form:
          obj = new MappingOriginForm();
          break;
        case ModelType.ObjectModel:
          obj = new MappingOriginObjectModel();
          break;
        case ModelType.FormElement:
          break;
        case ModelType.SObject:
          obj = new MappingOriginSalesforce();
          break;
        default:
          // do not assertNever here - sometimes mapping is created without need for source/destination models
          break;
      }

      if (obj) obj._nameInParent = 'destination';

      return obj;
    }
  };


  model_mappings: Hash<ModelMapping> = {};
  get modelMappingsArray(): ModelMapping[] {
    return ObjectHelper.toArray(this.model_mappings)
      .sort((a, b) => a.sort_order - b.sort_order);
  }

  _modelMappingHandler: ModelMappingHandler<ModelMapping> = new ModelMappingHandler(this, ModelMapping, 'model_mappings');
  get modelMappingHandler(): ModelMappingHandler<ModelMapping> {
    return this._modelMappingHandler;
  }

  updatePositionsForParent(parent: Mapping, item: ModelMapping | ModelMappingValue, position: number): void {
    this.modelMappingHandler.updateElementPositions(parent, item, position);
  }

  initializeEmbedded<T extends ModelMapping>(parent: Mapping, item: T, position: number, json: object = {}) {
    this.updatePositionsForParent(parent, item, position);
    this.placeInDictionary(item, item.rootName, this)
    parent.trackCreationDeletion(item.client_id, null, item.asJson(), item);
    this.saveChangesToStore();
    return item;
  }

  modelMappingsWithSource(sourceClientId: string) {
    return Object.keys(this.model_mappings).filter(key => this.model_mappings[key].source_identifier === sourceClientId).map(key => this.model_mappings[key])
  }

  set organization(val: Organization) {
    this._organization = val;
  }

  get organization(): Organization {
    if (this._organization) {
      // for TrackedModel implementation
      return this._organization;
    } else if (this.parent instanceof Organization) {
      // for OrganizationDocument implementation
      return this.parent;
    } else {
      return this.parent.organization;
    }
  }

  get classKey(): string {
    return this._classKey;
  }

  get storeName(): string {
    return `${this.rootName}Store`;
  }

  get titleOrDefault(): string {
    if (this.title && this.title.length > 0) {
      return this.title;
    } else {
      return "Untitled Mapping";
    }
  }

  static repo: IRepo<Mapping, MappingProvider> = new Repo<Mapping, MappingProvider>('mappings', Mapping, {}, new MappingProvider(Mapping, 'mapping', 'mappings'));
  repo: IRepo<this, MappingProvider<this>>;

  static get provider(): MappingProvider {
    return this.repo.provider;
  }

  get provider(): this['repo']['provider'] {
    return this.repo.provider;
  }

  static get displayName(): string {
    return 'Mapping';
  }

  get collectionKey() {
    return "mappings";
  }

  get UNTRACKED_PROPERTIES(): string[] {
    return [
      'u_at',
    ];
  }

  get rootName() {
    return 'mapping';
  }

  // ******************** VALIDATABLE******************** //
  _validation: MappingBuilderValidation<Mapping> = new MappingBuilderValidation(this);

  getDisplayName(propName: string, value?): string {
    switch (propName) {
      default:
        return propName;
    }
  }
  // ******************** VALIDATABLE END******************** //

//   // ******************** TRACK CHANGES******************** //

  get hasChanged() {
    return this._hasChanged;
  }

  set hasChanged(newVal: boolean) {
    if (newVal) {
      this.queueSendChangesToServer(this.lastPeekerOptions || {area: this.currentBuilderArea(), modelId: this.clientIdOrId});
      if (this.attempted_to_publish) {
        this.validate();
      }
    }
    this._hasChanged = newVal;
  }

  saveToServer(): Promise<any> {
    return store.dispatch(`${this.storeName}/saveToServer`).then(res => {
      this.handleErrorsAndUpdateSaveMessage(res.better_errors)
    }).catch(err => {
      ErrorHandler.autosaveCatch(this, err);
    });
  }

  protected saveChanges(changes: any, peekerOptions: string | IPeekerOptions = {area: this.currentBuilderArea(), modelId: this.clientIdOrId}): void {
    store.commit(`${this.storeName}/saveChanges`, {changes, peekerOptions});
  }

  // ******************** TRACK CHANGES END ******************** //

  get isPublished(): boolean {
    return this.is_published && Date.now() > Date.parse(this.u_at);
  }

  get isArchive(): boolean {
    return this.parent instanceof Folder && (this.parent.name === 'Archive' || this.parent.name === 'Trash');
  }

  canMoveTo(folder: Folder): boolean {
    return !folder.isArchiveFolder();
  }

  createModelMapping(source: IOriginWrapper, destination: IOriginWrapper): void {
    let newModelMapping = this.createEmbeddedObject.model_mappings(this, {});
    newModelMapping.initialize(this, this, null, {sourceParent: source, destinationParent: destination, position: this.nextSortOrderFor(ModelMapping.rootName)});
    this.initializeEmbedded(this, newModelMapping, this.nextSortOrderFor(ModelMapping.rootName));
  }

  clearAndResetModelMappings(): void {
    this.modelMappingsArray.forEach(modelMapping => modelMapping.destroy());
    this.createModelMapping(this.source, this.destination);
  }

  clearModelMappingsFromModelDeletion(deletedModelIdentifiers: string[], sourceOrDestination: 'source' | 'destination'): void {
    this.modelMappingsArray.forEach(modelMapping => {
      if (deletedModelIdentifiers.includes(modelMapping[`${sourceOrDestination}_identifier`])) {
        modelMapping.destroy();
      }
    })
  }

  get publishedProvider(): PublishedMappingProvider<Mapping> {
    return new PublishedMappingProvider(Mapping, 'published_mapping', 'published_mappings')
  }

  async fetchPublishedRecord(): Promise<any> {
    let response
    if (this.published_mapping_id) {
      response = await this.publishedProvider.fetchOne(this.published_mapping_id)
    } else {
      response = await this.provider.fetchPublished(this)
    }
    const data = response[0]
    if (data.success) {
      const json = data[this.publishedProvider.modelRoot]
      if (json) {
        this.buildPublishedRecord(json)
      }
    } else {
      if (import.meta.env.MODE === 'development') {
        Logger.error('Published Mapping', 'Published mapping request did not succeed', response)
      }
    }
    return response
  }

  buildPublishedRecord(json: Partial<IObjectModel> = {}): this['_publishedRecord'] {
    Vue.set(this, '_publishedRecord', new PublishedObjectModelDecorator().updateFrom(json))
    return this.publishedRecord
  }

  get publishedRecord(): this['_publishedRecord'] {
    return this._publishedRecord
  }

  buildNewSourceDestination({source, destination}: {source?: object, destination?: object}): this {
    if (source) this.source = this.createEmbeddedObject.source(this.mapping, source).loadFromJson(source)
    if (destination) this.destination = this.createEmbeddedObject.destination(this.mapping, destination).loadFromJson(destination)
    return this
  }

  modelMappingSourceOptions(): IOriginWrapper[] {
    return [this.source.root].concat(this.source.relationshipModels)
  }

  /**
   * Find destination options selectable for this source
   * @param source {IOriginWrapper}
   */
  modelMappingDestinationOptions(source: IOriginWrapper | null): IOriginWrapper[] {
    // Finds lv.2 destinations that aren't already being mapped to selected lv.2 source
    const modelMappingsWithSource = this.modelMappingHandler.withSource(source.rootIdentifier);
    const allOptions = [this.destination.root].concat(this.destination.relationshipModels);
    return allOptions.filter(destination => {
      return !modelMappingsWithSource.find(modelMapping => {
        return destination.rootIdentifier === modelMapping.destination_identifier
      })
    });
  }

  _moving: boolean = false
  get moving(): boolean {
    return this._moving
  }

  set moving(val: boolean) {
    this._moving = val
  }
}
